/**
 * State of AI Agents - Main Lab Component
 *
 * An interactive visualization showing the explosion of AI agent startups
 * based on analysis of Y Combinator companies.
 *
 * Primary experience: Growth chart, key metrics, domain breakdown
 * Secondary experience: Company explorer with filters and detailed views
 */

import React, { useState, useMemo } from "react";
import { TrendingUp, BookOpen, Loader2 } from "lucide-react";
import {
  useAnalysisData,
  useFilteredCompanies,
  useComputedMetrics,
} from "./useAnalysisData";
import { GrowthChart } from "./GrowthChart";
import { MetricCard } from "./MetricCard";
import { DomainChart } from "./DomainChart";
import { YearRangeSlider } from "./YearRangeSlider";
import { CompanyExplorer } from "./CompanyExplorer";
import type { FilterState } from "./types";

const MIN_YEAR = 2020;
const MAX_YEAR = 2025;

export const StateOfAgents: React.FC = () => {
  const { data, loading, error } = useAnalysisData();

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    yearRange: [MIN_YEAR, MAX_YEAR],
    selectedDomain: null,
    selectedSubdomain: null,
    searchTerm: "",
    showAgentsOnly: true,
  });

  // Compute metrics based on current filters (pass yoy_trends for all YC company totals)
  const metrics = useComputedMetrics(
    data?.companies || [],
    filters.yearRange,
    data?.summary?.yoy_trends
  );

  // Get filtered companies for explorer
  const filteredCompanies = useFilteredCompanies(
    data?.companies || [],
    filters
  );

  // Compute explorer-specific domain counts (after year filter but before domain filter)
  const explorerDomains = useMemo(() => {
    if (!data?.companies) return [];

    const companiesInRange = data.companies.filter((c) => {
      if (!c.launched_at) return false;
      const year = new Date(c.launched_at).getFullYear();
      return year >= filters.yearRange[0] && year <= filters.yearRange[1];
    });

    const agents = companiesInRange.filter((c) => c.is_agent);

    const counts: Record<string, number> = {};
    agents.forEach((c) => {
      const domain = c.domain || "other";
      counts[domain] = (counts[domain] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([domain, count]) => ({ domain, count }))
      .sort((a, b) => b.count - a.count);
  }, [data?.companies, filters.yearRange]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto" />
          <p className="text-secondary">Loading analysis data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-red-500">Failed to load analysis data</p>
          <p className="text-secondary text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const summary = data.summary.executive_summary;

  return (
    <div className="space-y-10">
      {/* ============ PRIMARY EXPERIENCE ============ */}

      {/* Main visualization: Stats left, Chart right */}
      <section className="bg-secondary rounded-lg p-5">
        {/* Compact slider at top */}
        <div className="mb-4 max-w-md">
          <YearRangeSlider
            value={filters.yearRange}
            min={MIN_YEAR}
            max={MAX_YEAR}
            onChange={(yearRange) => setFilters((f) => ({ ...f, yearRange }))}
            compact
          />
        </div>

        {/* Stats and Chart in same row */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 lg:min-h-[280px]">
          {/* Stats column */}
          <div className="flex flex-row lg:flex-col gap-3 lg:gap-4">
            <MetricCard
              value={metrics.totalCompanies.toLocaleString()}
              label="AI Companies"
              sublabel={`${filters.yearRange[0]}-${filters.yearRange[1]}`}
            />
            <MetricCard
              value={metrics.agentCompanies.toLocaleString()}
              label="Agent Companies"
              sublabel={`${filters.yearRange[0]}-${filters.yearRange[1]}`}
            />
            <MetricCard
              value={Number(metrics.startYearPct) > 0
                ? `+${Math.round(((Number(metrics.endYearPct) - Number(metrics.startYearPct)) / Number(metrics.startYearPct)) * 100)}%`
                : '-'}
              label="Agent Startup Growth"
              sublabel={`${metrics.startYearPct}% → ${metrics.endYearPct}% of YC`}
            />
          </div>

          {/* Chart column */}
          <div className="min-w-0 h-full">
            <GrowthChart
              yearData={metrics.yearData}
              yearRange={filters.yearRange}
            />
          </div>
        </div>
      </section>

      {/* Domain breakdown */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-primary">
              Agent Domains
            </h2>
            <p className="text-xs text-secondary mt-0.5">
              Industry domains where YC startups are building AI agents
            </p>
          </div>
          <DomainChart
            domains={metrics.domains}
            totalCount={metrics.agentCompanies}
            onDomainClick={(domain) =>
              setFilters((f) => ({
                ...f,
                selectedDomain: domain,
                selectedSubdomain: null,
              }))
            }
            selectedDomain={filters.selectedDomain}
          />
        </div>

        {/* Right column: Methodology on top, then Subdomains */}
        <div className="space-y-4">
          {/* Methodology teaser - always on top */}
          <div className="bg-secondary rounded-lg p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-primary mb-1">
                Data Analysis Methodology
              </h3>
              <p className="text-secondary text-xs mb-2">
                4-stage multi-agent workflow: data loading, pre-filtering (68%
                cost reduction), LLM classification, and insight generation.
                Cost: $56 for 1,795 companies.
              </p>
              <a
                href="/#book"
                className="inline-flex items-center gap-1 text-xs text-accent font-medium hover:underline"
              >
                Learn the patterns in Chapter 14
                <TrendingUp className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Subdomain pills - show when domain is selected */}
          {filters.selectedDomain && (
            <div className="bg-secondary rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-primary">
                  {filters.selectedDomain
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                  Subcategories
                </h3>
                <button
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      selectedDomain: null,
                      selectedSubdomain: null,
                    }))
                  }
                  className="text-xs text-secondary hover:text-primary"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {metrics.subdomains
                  .filter((s) => {
                    // Find companies with this subdomain and check if they match selected domain
                    const matchingCompanies = data?.companies?.filter(
                      (c) =>
                        c.subdomain === s.subdomain &&
                        c.domain === filters.selectedDomain &&
                        c.is_agent
                    );
                    return matchingCompanies && matchingCompanies.length > 0;
                  })
                  .slice(0, 12)
                  .map(({ subdomain, count }) => {
                    // Recalculate count for this domain
                    const domainCount =
                      data?.companies?.filter(
                        (c) =>
                          c.subdomain === subdomain &&
                          c.domain === filters.selectedDomain &&
                          c.is_agent
                      ).length || 0;
                    if (domainCount === 0) return null;
                    const isSelected = filters.selectedSubdomain === subdomain;
                    return (
                      <button
                        key={subdomain}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            selectedSubdomain:
                              f.selectedSubdomain === subdomain
                                ? null
                                : subdomain,
                          }))
                        }
                        className={`px-2 py-1 text-xs rounded-md transition-colors ${
                          isSelected
                            ? "bg-accent text-white"
                            : "bg-tertiary text-secondary hover:bg-accent/20 hover:text-primary"
                        }`}
                      >
                        {subdomain} ({domainCount})
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ SECONDARY EXPERIENCE ============ */}

      <hr className="border-secondary" />

      {/* Company explorer */}
      <section>
        <CompanyExplorer
          companies={filteredCompanies}
          domains={explorerDomains}
          selectedDomain={filters.selectedDomain}
          onDomainSelect={(domain) =>
            setFilters((f) => ({ ...f, selectedDomain: domain }))
          }
          searchTerm={filters.searchTerm}
          onSearchChange={(searchTerm) =>
            setFilters((f) => ({ ...f, searchTerm }))
          }
        />
      </section>

      {/* Footer with data source */}
      <section className="text-center py-8 border-t border-secondary">
        <p className="text-sm text-secondary">
          Data from Y Combinator companies • Last updated:{" "}
          {new Date(data.last_updated).toLocaleDateString()} •{" "}
        </p>
      </section>
    </div>
  );
};

export default StateOfAgents;
